import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, ChevronRight, Star, Leaf, Sparkles, ArrowRight, User, Gift } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import WhatsAppFab from "@/components/WhatsAppFab";
import AltosLoginDialog from "@/components/AltosLoginDialog";
import { api, resolveImageUri, formatINR } from "@/lib/api";
import { getPriceInfo, orderCategories } from "@/lib/pricing";
import { categoryVisual } from "@/lib/categoryIcons";
import { useStore } from "@/context/StoreContext";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFab";

function Row({ title, icon, items, verified, onViewAll, testid }) {
  const navigate = useNavigate();
  if (!items.length) return null;
  return (
    <section className="mt-10" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-3xl text-ink flex items-center gap-2.5"><span className="w-1 h-7 bg-crimson rounded-full" />{icon}{title}</h2>
        {onViewAll && (
          <button onClick={onViewAll} data-testid={`view-all-${title}`} className="text-sm font-semibold text-brand flex items-center gap-1 hover:gap-1.5 transition-all">
            View all <ChevronRight size={15} />
          </button>
        )}
      </div>
      <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1">
        {items.map((p) => {
          const info = getPriceInfo(p, verified);
          return (
            <div key={p.id} onClick={() => navigate(`/product/${p.id}`)} data-testid={`row-item-${p.id}`} className="shrink-0 w-40 sm:w-44 cursor-pointer group">
              <div className="w-full h-40 sm:h-44 bg-mist rounded-lg overflow-hidden">
                <img src={resolveImageUri(p.image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <p className="font-display text-lg leading-tight text-ink mt-2 line-clamp-2">{p.name}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm font-semibold text-ink">{formatINR(info.unit)}</span>
                {info.compareAt !== null && <span className="text-xs text-muted2 line-through">{formatINR(info.compareAt)}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function Home() {
  const { verified, altosName } = useStore();
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("All");
  const [loginOpen, setLoginOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) {
      setActive(cat);
      document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [searchParams]);

  const { data: products = [], isLoading } = useQuery({ queryKey: ["products", active], queryFn: () => api.listProducts(active) });
  const { data: rawCategories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.categories() });
  const categories = useMemo(() => orderCategories(rawCategories), [rawCategories]);
  const { data: banners = [] } = useQuery({ queryKey: ["banners"], queryFn: () => api.listBanners() });
  const { data: allProducts = [] } = useQuery({ queryKey: ["products", "All"], queryFn: () => api.listProducts("All") });
  const offerProducts = useMemo(() => allProducts.filter((p) => p.buy3get1), [allProducts]);

  const [bannerIdx, setBannerIdx] = useState(0);
  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % banners.length), 4000);
    return () => clearInterval(t);
  }, [banners.length]);

  const query = search.trim().toLowerCase();
  const searchResults = useMemo(
    () => (query ? products.filter((p) => p.name.toLowerCase().includes(query) || p.category.toLowerCase().includes(query)) : []),
    [query, products]
  );
  const bestsellers = !query && active === "All" ? products.filter((p) => p.bestseller || p.featured) : [];
  const gridItems = query ? searchResults : active === "All" ? [] : products;
  const showRows = !query && active === "All";

  return (
    <div className="min-h-screen bg-paper">
      <Header />

      {offerProducts.length > 0 && (
        <button onClick={() => navigate(`/product/${offerProducts[0].id}`)} data-testid="offer-ribbon" className="w-full bg-crimson text-white overflow-hidden group">
          <div className="mx-auto max-w-6xl px-4 py-2.5 flex items-center justify-center gap-2 sm:gap-3 text-sm font-semibold">
            <Gift size={18} className="shrink-0 animate-pulse" />
            <span className="tracking-wide">BUY 3 GET 1 FREE</span>
            <span className="hidden sm:inline opacity-90 font-normal truncate max-w-[40vw]">on {offerProducts.slice(0, 3).map((p) => p.name).join(", ")}{offerProducts.length > 3 ? " & more" : ""}</span>
            <span className="inline-flex items-center gap-1 underline underline-offset-2 group-hover:gap-2 transition-all shrink-0">Shop now <ArrowRight size={15} /></span>
          </div>
        </button>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-line">
        {banners.length > 0 ? (
          <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
            <div className="relative aspect-[16/7] w-full rounded-2xl overflow-hidden bg-mist" data-testid="hero-carousel">
              {banners.map((b, i) => (
                <img key={b.id} src={resolveImageUri(b.image)} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === bannerIdx ? "opacity-100" : "opacity-0"}`} />
              ))}
              {banners.length > 1 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                  {banners.map((b, i) => (
                    <button key={b.id} onClick={() => setBannerIdx(i)} data-testid={`banner-dot-${i}`} className={`h-2 rounded-full transition-all ${i === bannerIdx ? "w-6 bg-paper" : "w-2 bg-paper/60"}`} aria-label={`banner ${i + 1}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative overflow-hidden bg-charcoal hero-glow">
            <div className="mx-auto max-w-6xl px-6 py-20 sm:py-28 relative z-10">
              <div className="max-w-2xl fade-up">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-brandsec font-semibold mb-5">
                  <span className="w-6 h-px bg-crimson" /> <Leaf size={14} /> Rooted in Nature
                </span>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white">
                  Herbal wellness,<br />delivered with <span className="text-brandsec italic">care</span>.
                </h1>
                <div className="mt-5 h-1 w-24 bg-crimson rounded-full" />
                <p className="mt-6 text-base text-white/70 max-w-lg leading-relaxed">Genuine Altos herbal supplements & natural skincare. Special DP pricing for Altos ID holders.</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button onClick={() => document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 bg-brand text-white font-semibold rounded-full px-7 h-12 hover:bg-brandsec transition-colors" data-testid="hero-shop-cta">
                    Shop the collection <ArrowRight size={18} />
                  </button>
                  <button onClick={() => navigate("/concerns")} className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold rounded-full px-7 h-12 hover:bg-white/10 transition-colors">
                    Shop by Concern
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <main id="catalog" className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        {/* Altos DP banner */}
        {verified ? (
          <div className="flex items-center gap-3 rounded-xl border border-ok/30 bg-brandter px-4 py-3 mb-6" data-testid="altos-verified-banner">
            <Star className="text-ok fill-ok" size={18} />
            <p className="text-sm font-medium text-ink">{altosName ? <>Welcome back, <span className="font-semibold">{altosName}</span>! </> : "You're logged in as an "}<span className="font-semibold">{altosName ? "" : "Altos ID holder"}</span> — DP prices & BV points are unlocked.</p>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-line bg-mist px-4 py-3 mb-6">
            <p className="text-sm text-ink">Are you an Altos ID holder? Log in to unlock DP pricing.</p>
            <button onClick={() => setLoginOpen(true)} data-testid="home-altos-login" className="shrink-0 h-9 px-4 rounded-full bg-brand text-white text-sm font-semibold flex items-center gap-1.5">
              <User size={15} /> Log in
            </button>
          </div>
        )}

        {/* Feature shortcuts */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <button onClick={() => navigate("/concerns")} data-testid="home-concerns" className="flex items-center gap-3 rounded-xl border border-line bg-mist px-4 py-3 hover:border-brand transition-colors text-left">
            <Star className="text-brand" size={18} />
            <span className="flex-1 text-sm font-semibold text-ink">Shop by Concern</span>
            <ChevronRight className="text-muted2" size={17} />
          </button>
          <button onClick={() => navigate("/blogs")} data-testid="home-blogs" className="flex items-center gap-3 rounded-xl border border-line bg-mist px-4 py-3 hover:border-brand transition-colors text-left">
            <Sparkles className="text-brand" size={18} />
            <span className="flex-1 text-sm font-semibold text-ink">Blogs & Vlogs</span>
            <ChevronRight className="text-muted2" size={17} />
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted2" size={18} />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full h-12 rounded-full border border-line bg-paper pl-11 pr-11 text-sm focus:outline-none focus:border-brand"
          />
          {search && (
            <button data-testid="search-clear" onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted2"><X size={18} /></button>
          )}
        </div>

        {/* Category chips */}
        {!query && (
          <div className="flex gap-2 overflow-x-auto hide-scrollbar mt-6 justify-start sm:justify-center">
            {["All", ...categories].map((c) => {
              const vis = c === "All" ? null : categoryVisual(c);
              return (
                <button
                  key={c}
                  data-testid={`category-chip-${c}`}
                  onClick={() => setActive(c)}
                  className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium border transition-colors ${active === c ? "bg-brand text-white border-brand" : "bg-paper text-ink border-line hover:border-brand"}`}
                >
                  {vis && <vis.Icon size={14} style={{ color: active === c ? "#fff" : vis.color }} />}
                  {c}
                </button>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <div className="py-24 text-center text-muted2">Loading catalog…</div>
        ) : showRows ? (
          <>
            <Row title="Bestsellers" testid="bestsellers-row" icon={<span className="inline-flex w-9 h-9 rounded-xl items-center justify-center shadow-[0_4px_12px_-4px_rgba(0,0,0,0.25),inset_0_1px_1px_rgba(255,255,255,0.7)] ring-1 ring-black/5" style={{ background: "linear-gradient(145deg, #3E8E4C33, #3E8E4C12)" }}><Sparkles size={18} className="text-brand" /></span>} items={bestsellers} verified={verified} />
            {categories.map((cat) => {
              const items = products.filter((p) => p.category === cat);
              const { Icon, color } = categoryVisual(cat);
              return <Row key={cat} title={cat} testid={`cat-row-${cat}`} icon={<span className="inline-flex w-9 h-9 rounded-xl items-center justify-center shadow-[0_4px_12px_-4px_rgba(0,0,0,0.25),inset_0_1px_1px_rgba(255,255,255,0.7)] ring-1 ring-black/5" style={{ background: `linear-gradient(145deg, ${color}33, ${color}12)` }}><Icon size={18} style={{ color }} /></span>} items={items.slice(0, 10)} verified={verified} onViewAll={() => setActive(cat)} />;
            })}
          </>
        ) : (
          <>
            {active !== "All" && !query && (
              <div className="flex items-center justify-between mt-8 mb-2">
                <h2 className="font-display text-3xl text-ink">{active}</h2>
                <button onClick={() => setActive("All")} data-testid="back-to-all" className="text-sm font-semibold text-brand">All categories</button>
              </div>
            )}
            {gridItems.length === 0 ? (
              <div className="py-24 text-center text-muted2" data-testid="empty-state">No products found{query ? ` for "${search.trim()}"` : ""}.</div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 mt-6" data-testid="product-grid">
                {gridItems.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </>
        )}

        {/* Bulk orders */}
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Altos World, I'd like to place a bulk order.")}`}
          target="_blank" rel="noopener noreferrer"
          data-testid="bulk-order-button"
          className="mt-12 flex items-center gap-4 bg-[#25D366] text-white rounded-xl px-5 py-4 hover:brightness-105 transition"
        >
          <div className="flex-1">
            <p className="font-semibold">For Bulk Orders — Click Here</p>
            <p className="text-sm text-white/85">Chat directly with us on WhatsApp</p>
          </div>
          <ChevronRight size={20} />
        </a>
      </main>

      <Footer />
      <WhatsAppFab />
      <AltosLoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
